import { expOn, ambientLevel } from "./experience";

let current = null;

export const soundEnabled = () =>
  sessionStorage.getItem("rw_sound") === "1" && sessionStorage.getItem("rj_interacted") === "1";

export function initInteraction() {
  if (sessionStorage.getItem("rj_interacted") === "1") return;
  const mark = () => {
    sessionStorage.setItem("rj_interacted", "1");
    if (!sessionStorage.getItem("rw_sound")) sessionStorage.setItem("rw_sound", "1");
    window.removeEventListener("pointerdown", mark);
    window.removeEventListener("keydown", mark);
  };
  window.addEventListener("pointerdown", mark);
  window.addEventListener("keydown", mark);
}

// First vs return visit: auto-narration only plays during a visitor's first-ever session
export function initVisitTracking() {
  if (!sessionStorage.getItem("rj_session_type")) {
    sessionStorage.setItem("rj_session_type", localStorage.getItem("rj_visited") === "1" ? "return" : "first");
    localStorage.setItem("rj_visited", "1");
  }
}

export const isFirstVisit = () => sessionStorage.getItem("rj_session_type") === "first";

// Shared 432 Hz ambient hum — one element continues across the vault and world pages.
// Level and on/off are owner-controlled via Admin → Experience Controls.
let _amb = null;
let _ambIv = null;
let _ambKind = "vault";

const _ambRetry = () => {
  window.removeEventListener("pointerdown", _ambRetry);
  window.removeEventListener("touchstart", _ambRetry);
  if (sessionStorage.getItem("rw_sound") === "1") startAmbient(_ambKind);
};

function armAmbientRetry() {
  window.addEventListener("pointerdown", _ambRetry, { once: true });
  window.addEventListener("touchstart", _ambRetry, { once: true, passive: true });
}

function rampAmbient(target, step) {
  clearInterval(_ambIv);
  _ambIv = setInterval(() => {
    if (!_amb) return clearInterval(_ambIv);
    const d = target - _amb.volume;
    if (Math.abs(d) <= step) {
      _amb.volume = target;
      clearInterval(_ambIv);
      if (target === 0) _amb.pause();
      return;
    }
    _amb.volume = Math.min(1, Math.max(0, _amb.volume + (d > 0 ? step : -step)));
  }, 80);
}

export function startAmbient(kind = "vault") {
  if (!expOn("ambientSound")) return;
  _ambKind = kind;
  if (sessionStorage.getItem("rw_sound") !== "1") {
    armAmbientRetry();
    return;
  }
  try {
    if (!_amb) {
      _amb = new Audio("/audio/vault-ambient.wav");
      _amb.loop = true;
      _amb.volume = 0;
    }
    if (_amb.paused) {
      _amb.play().catch(() => armAmbientRetry());
    }
    rampAmbient(ambientLevel(kind), 0.02);
  } catch (e) {
    console.debug("ambient failed:", e?.message);
  }
}

export function stopAmbient(fast) {
  window.removeEventListener("pointerdown", _ambRetry);
  window.removeEventListener("touchstart", _ambRetry);
  if (!_amb) return;
  rampAmbient(0, fast ? 0.06 : 0.025);
}

export function playVoice(src, volume = 0.55) {
  if (!soundEnabled() || !expOn("voiceNarration")) return null;
  try {
    if (current) current.pause();
    current = new Audio(src);
    current.volume = volume;
    current.play().catch(() => {});
    return current;
  } catch (e) {
    console.debug("voice failed:", e?.message);
    return null;
  }
}

export function stopVoice() {
  if (current) {
    current.pause();
    current = null;
  }
}

// Split narration into sentences with start offsets weighted by length across totalMs
export function sentenceTimings(text, totalMs) {
  const parts = text.match(/[^.!?]+[.!?]+["'\u201d]?/g) || [text];
  const sentences = parts.map((s) => s.trim()).filter(Boolean);
  const totalChars = sentences.reduce((a, s) => a + s.length, 0) || 1;
  let acc = 0;
  return sentences.map((s) => {
    const seg = { text: s, start: acc };
    acc += (s.length / totalChars) * totalMs;
    return seg;
  });
}
